/*!
 * concat.managers.VTT
 * 
 * version: 1.0.0
 * author: John Bandy, john@concatsys.com
 * company: Concat Systems, Inc.
 * git: https://github.com/ConcatSystems/SharedLibs
 *
 * requirements:
 *   - jQuery ($ or concat.$)
 *   - WebVTT (WebVTT)
 *
 * ©2019 Concat Systems, Inc.
 */


( ( window, undefined ) => {

  // set up namespace
  window.concat = window.concat || {};
  window.concat.managers = window.concat.managers || {};

  // already been loaded
  if( window.concat.managers.VTT ) { return; }

  // main VTT "class" object
  window.concat.managers.VTT = ( function() {

      var _$              = undefined;
      var _trackIDs       = [];
      var _trackIDsByType = {};
      var _tracks         = {};

      /**
       * _filterCue
       *
       * @param {cue} an WebVTTCue object
       *
       * @returns {WebVTTCue} 
       */
      //------------------------------------------------------------------------
      var _filterCue = function( cue ) {
          var obj = {};

          // Filter out getCueAsHTML as it's a function and hasBeenReset and displayState as
          // they're only used when running the processing model algorithm.
          Object.keys( cue ).forEach( function( key ) 
          {
            if( key !== "getCueAsHTML" && 
                key !== "hasBeenReset" && 
                key !== "displayState" ) 
            {
              obj[key] = cue[key];
            }
          });
          return obj;
        }

      /**
       * _parseThumbImage
       *
       * @param {imgURI} an image URI
       *
       * @returns {object} an object with src, x, y, w, h properties
       */
      //----------------------------------------------------------------------
      var _parseThumbImage = function( imgURI ) {
          // concat.utils.Log( "vtt :: parseImage(), imgURI:", imgURI );

          var src;
          var hashindex = imgURI.indexOf( '#' );
          var hash      = imgURI.substring( hashindex + 1 );
          var retval    = {
                src     : imgURI,
                x       : 0, 
                y       : 0,
                w       : 0,
                h       : 0
              };

          // no hash
          if( hashindex === -1 ) { 
            return retval; 
          }

          // splitting source from hash
          src = imgURI.substring( 0, hashindex );
          retval.src = src;

          // if hash is not correct format...
          if( hash.substring( 0, 5 ) !== 'xywh=' ) 
            return retval;

          // hash is correct, handle accordingly
          var p = hash.substring( 5 ).split( ',' );
          retval.x   = parseInt( p[0] );
          retval.y   = parseInt( p[1] );
          retval.w   = parseInt( p[2] );
          retval.h   = parseInt( p[3] );

          return retval;
        }

      /**
       * _getThumbImgCSSForCoords
       *
       * @param {x} x-position
       * @param {y} y-position
       * @param {w} width
       * @param {h} height
       *
       * @returns {Object} a CSS object
       */
      //------------------------------------------------------------------------
      var _getThumbImgCSSForCoords = function( x, y, w, h ) {
          var left = ( x !== 0 ? "-" : "" ) + `${x}px`;
          var top  = ( y !== 0 ? "-" : "" ) + `${y}px`;
          var clip = `rect(${y}px ${(x+w)}px ${(y+h)}px ${x}px)`;

          return {
            "left" : left,
            "top"  : top,
            "clip" : clip
          }
        }

      /**
       * _insertCueByStartTime
       *
       * @param {cues} an array of WebVTTCue objects
       * @param {cue} a WebVTTCue object
       * @param {min} the minimum index of the subset array
       * @param {max} the maximum index of the subset array
       */
      //------------------------------------------------------------------------
      var _insertCueByStartTime = function( cues, cue, min, max ) {

          // if the array is empty, or if items are already sorted from the 
          // input, let's do this check for a base-case complexity of O(1)...
          if( max === -1 || ( max === cues.length - 1 && cue.startTime > cues[max].startTime ) )
          {            
            cues.push( cue );
            return;
          }
          if( min === 0 && cue.startTime < cues[min].startTime )
          {
            cues.unshift( cue );
            return;
          }

          var mid = min + Math.floor( ( max - min ) / 2 );
          // concat.utils.Log( "  min:", min, "max:", max, " --> mid:", mid, cues[mid].startTime );

          // otherwise, do a recursive binanry search for the insertion point 
          // for a worst-case complexity of O(log n)

          // is the current startTime between the startTimes of the cues at 
          // `mid` and `mid+1`?
          if( cue.startTime > cues[mid].startTime && 
              cue.startTime < cues[mid+1].startTime )
            cues.splice( mid+1, 0, cue );

          // no? try searching the next appropriate 'half' of the array
          else if( cue.startTime < cues[mid].startTime )
            _insertCueByStartTime( cues, cue, min, mid-1 )

          else if( cue.startTime > cues[mid+1].startTime )
            _insertCueByStartTime( cues, cue, mid+1, max );
        }

      /**
       * _searchCuesByTime
       *
       * @param {time} time, in seconds, to search within the array of cues
       * @param {cues} an array of WebVTTCue objects
       * @param {min} the minimum index of the subset array
       * @param {max} the maximum index of the subset array
       *
       * @returns {WebVTTCue} 
       */
      //------------------------------------------------------------------------
      var _searchCuesByTime = function( time, cues, min, max ) {

          var mid = min + Math.ceil( ( max - min ) / 2 );
          var midCue = cues[mid]

          if( !midCue ) return( null );

          // concat.utils.Log( "VTT :: _searchCuesByTime(), time:", time );
          // concat.utils.Log( "  min:", min, "max:", max, " --> mid:", mid, ":", midCue.startTime, "-", midCue.endTime );


          // is the time in the cue's range at `mid`?
          if( midCue.startTime <= time && time <= midCue.endTime )
          {
            // concat.utils.Log( `  time is in range, ${midCue.startTime} <= ${time} <= ${midCue.endTime}` );
            return( midCue );
          }

          // no? try searching the next appropriate 'half' of the array
          else {
            if( min === mid )
            {
              // concat.utils.Log( "  min === mid, returning null" );
              return( null );
            }
            else if( time < midCue.startTime )
            {
              // concat.utils.Log( `  time < mid.startTime ( ${time} < ${midCue.startTime} )` );
              return( _searchCuesByTime( time, cues, min, mid - 1 ) );
            }
            else if( time > midCue.endTime )
            {
              // concat.utils.Log( `  time > mid.endTime ( ${time} > ${midCue.endTime} )` );
              return( _searchCuesByTime( time, cues, mid + 1, max ) );
            }
          }
        }

      /**
       * _deleteTrack
       *
       * @param {id} unique track identifier
       */
      //------------------------------------------------------------------------
      var _deleteTrack = function( id ) {

          if( !_tracks[id] ) return;

          // remove id from _trackIDs
          var i;
          for( i = 0; i < _trackIDs.length; i++ )
            if( _trackIDs[i] === id )
              _trackIDs.splice( i, 1 );

          // remove id from _trackIDsByType
          var t = _trackIDsByType[_tracks[id].type];
          for( i = 0; i < t.length; i++ )
            if( t[i] === id )
              t.splice( i, 1 );

          // finally, remove the track
          _tracks[id] = null;
        }

      //------------------------------------------------------------------------
      return {

        types : {
          CAPTIONS : "captions",
          THUMBNAILS : "thumbnails",
          SEEKTHUMBS : "seekthumbs"
        },

        /**
         * getTrack
         *
         * @param {id} track identifier 
         *
         * @returns {object} 
         */
        //----------------------------------------------------------------------
        getTrack : function( id ) {
            return _tracks[id];
          },

        /**
         * getTracksByType
         *
         * @param {type} track type identifier 
         *
         * @returns {array} 
         */
        //----------------------------------------------------------------------
        getTracksByType : function( type ) {
            concat.utils.Log( "VTT :: getTracksByType(), type:", type )
            var a = [];
            var ids = _trackIDsByType[type];

            for( var i = 0; ids && i < ids.length; i++ )
              a.push( _tracks[ ids[i] ] );

            return( a );
          },

        /**
         * loadTrackFile
         *
         * @param {id} track identifier 
         * @param {src} file URI
         *
         * @returns {Promise} 
         */
        //----------------------------------------------------------------------
        loadTrackFile : function( id, type, src ) {
            concat.utils.Log( "VTT :: loadTrackFile(), Called. id:", id );

            var p = new Promise( function( resolve, reject ) {

              // validation checks
              if( typeof window.WebVTT === "undefined" )
                                    { concat.utils.Log( "  no WebVTT, rejecting..." );    reject( { e: new Error( "VTT :: loadTrackFile(), Error: required library WebVTT not found" ), id: id }); return; }
              if( !concat.$ )       { concat.utils.Log( "  no jQuery, rejecting..." );    reject( { e: new Error( "VTT :: loadTrackFile(), Error: required library jQuery not found" ), id: id }); return; }
              if( id === "" )       { concat.utils.Log( "  no id, rejecting..." );        reject( { e: new Error( "VTT :: loadTrackFile(), Error: `id` is blank" ), id: id }); return; }
              if( src === "" )      { concat.utils.Log( "  no src, rejecting..." );       reject( { e: new Error( "VTT :: loadTrackFile(), Error: `src` is blank" ), id: id }); return; }
              if( _tracks[id] )     { concat.utils.Log( "  duplicate ID, rejecting..." ); reject( { e: new Error( "VTT :: loadTrackFile(), Error: duplicate ID"), id: id }); return; }

              // set up jquery
              _$ = concat.$;

              // track recall management
              _tracks[id] = {
                id   : id,
                type : type,
                cues : []
              };
              _trackIDs.push( id );
              if( !_trackIDsByType[type] )
                _trackIDsByType[type] = [];
              _trackIDsByType[type].push( id );

              // init the WebVTT parser
              var parser = new WebVTT.Parser( window, WebVTT.StringDecoder() );
              parser.oncue = function( cue ) 
                {
                  // concat.utils.Log( "VTT :: _parser.oncue(), Fired. id:", id, "cue:", cue );
                  if( type === "thumbnails" || 
                      type === "seekthumbs" )
                  {
                    var p = _parseThumbImage( cue.text );
                    cue.thumb = p;
                    cue.thumb.css = _getThumbImgCSSForCoords( p.x, p.y, p.w, p.h );
                  }

                  _insertCueByStartTime( _tracks[id].cues, _filterCue( cue ), 0, _tracks[id].cues.length-1 );
                };
              parser.onparsingerror = function( e ) 
                {
                  // concat.utils.Log( "VTT :: _parser.onparsingerror(), Fired. id:", id, "rejecting..." );
                  _deleteTrack( id );
                  reject({ e: new Error( "VTT :: loadTrackFile(), Error: parsing error" ), id: id });
                };
              parser.onflush = function() 
                {
                  // concat.utils.Log( "VTT :: _parser.onflush(), Fired. id:", id, "resolving..." );
                  // _tracks[id] && concat.utils.Log ( "  cues:", _tracks[id].cues );
                  resolve();
                };

              // concat.utils.Log( "  parser:", parser );
              // concat.utils.Log( "  src:", src );
              // concat.utils.Log( "  _$:", _$ );
              // concat.utils.Log( "  _$.get:", _$.get );

              // concat.utils.Log( "  $.getting..." );

              _$.get({ 
                url      : src,
                dataType : "text"
              })
                .done( function( data, textStatus, jqXHR ) {
                  // concat.utils.Log( "VTT :: _$.get.done(), Fired. id:", id );
                  // handle data
                  parser.parse( data );
                  parser.flush();
                  // concat.utils.Log( "VTT :: _$.get.done(), Ended." );
                })
                .fail( function( jqXHR, textStatus, errorThrown ) {
                  // concat.utils.Log( "VTT : _$.get.fail(), Fired. id:", id, "rejecting..." );
                  // concat.utils.Log( "  jqXHR:", jqXHR );
                  // concat.utils.Log( "  textStatus:", textStatus );
                  // concat.utils.Log( "  errorThrown:", errorThrown );
                  _deleteTrack( id );
                  reject({ e: new Error( `VTT :: loadTrackFile(), Error: file load failed` ), id: id });
                });
            });

            return( p );
          },

        /**
         * VTT.getCueForTime
         *
         * @param {track} a WebVTTTrack item
         * @param {time} time, in seconds
         *
         * @returns {WebVTTCue} a WebVTTCue or null
         */
        //----------------------------------------------------------------------
        getCueForTime : function( trackID, time ) {
            // concat.utils.Log( "VTT :: getCueForTime(), trackID:", trackID, "time:", time );

            if( time < 0 ||
                !trackID ||
                trackID === "" || 
                !_tracks[trackID] ||
                !_tracks[trackID].cues ) 
            { return null; }

            var cues = _tracks[trackID].cues
            return( _searchCuesByTime( time, cues, 0, cues.length - 1 ) );
          },

        /**
         * VTT.generateFileContentForRegularThumbnails
         * 
         * @requirements concat.utils.SecsToTimecode()
         *
         * @param {duration} the duration of the media
         * @param {src} base source file/path for the thumbnail sprite sheet
         * @param {cols} number of columns in the sprite sheet 
         * @param {rows} number of rows in the sprite sheet
         * @param {width} width, in whole pixels, of each thumbnail in the sprite sheet
         * @param {height} height, in whole pixels, of each thumbnail in the sprite sheet
         *
         * @returns null
         */
        //------------------------------------------------------------------------
        generateFileContentForRegularThumbnails( options ) {
            concat.utils.Log( "VTT :: generateFileContentForRegularThumbnails(), options:", options );

            var o  = options;
            var t  = o.columns * o.rows,
            dt = o.duration / t;

            // console.log( "  t:", t );
            // console.log( "  dt:", dt );

            var f = function( n ) 
            {
              let ms = Number( n - Math.floor( n ) ).toFixed( 3 ) * 1000;
              n = Math.floor( n );
              let mm = Math.floor( n / 60 );
              mm ? n = n % ( mm * 60 ) : n;
              let ss = n;

              return( `${mm.pad()}:${ss.pad()}.${ms.pad(3)}`);
            }

            for( var i = 0; i < t; i++ )
            {
              console.log( `${f(i*dt)} --> ${f(i*dt+dt)}` );
              console.log( `${o.src}#xywh=${(i%o.columns)*o.width},${Math.floor(i/o.columns)*o.height},${o.width},${o.height}` );
            }
          }
      } // return {...}
    })();

})( window );